import {
  Select,
  Button,
  FormControl,
  OutlinedInput,
  InputLabel,
  Stack,
  ListItemIcon,
  Typography,
  MenuItem,
  Autocomplete,
  ListItemText,
  styled,
  Divider,
} from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchIcon from "@mui/icons-material/Search";

import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import AssetIcon from "../../Misc/AssetIcon/asset-icon";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { useEffect, useState } from "react";
import { DbIngestConfigPackInfos } from "../../../Core/Api/Types/db-ingest-config-pack-infos";
import { ingestService } from "../../../Core/Api/services";
import { useNavigate } from "react-router";
import { routePaths } from "../../../Router/router";
import { DbIngestConfigPackPlaceholderRowInfos } from "../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-infos";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  // confirm: boolean;
};

export enum IngestConfigurePlaceholderContext {
  Create,
  Update,
  Duplicate,
}

export type ModalCreatePlaceholderElementProps = {
  onClose?: (props: CloseClbProps) => void;
  inputPack?: DbIngestConfigPackInfos;
  inputPlaceholder?: DbIngestConfigPackPlaceholderRowInfos;
  context: IngestConfigurePlaceholderContext;
};

export type ModalCreatePlaceholderProps = CommonModalProps<ModalCreatePlaceholderElementProps>;

const ModalCreateOrUpdatePlaceholder = React.forwardRef<any, ModalCreatePlaceholderProps>((props, ref) => {
  const { closeModal, onClose, inputPack, inputPlaceholder, context } = props;
  const { t } = useTranslation();
  const { active: placeholderOptional, toggle: togglePlaceholderOptional } = useToggle(
    inputPlaceholder?.isOptionnal ?? false
  );
  const [objectType, setObjectType] = React.useState<ObjectType>(ObjectType.Video);
  const [name, setName] = React.useState("");
  const [desc, setDesc] = React.useState("");
  const [pack, setPack] = React.useState<DbIngestConfigPackInfos>();
  const [classes, setClasses] = React.useState<ObjectClass[]>([]);
  const [packs, setPacks] = useState<DbIngestConfigPackInfos[]>([]);

  const navigate = useNavigate();

  const handleCloseClick: React.MouseEventHandler = () => {
    onClose && onClose({ confirm: false });
    closeModal();

    let route =
      context === IngestConfigurePlaceholderContext.Create
        ? "/new"
        : (context === IngestConfigurePlaceholderContext.Update ? "/update" : "/duplicate") +
          "/" +
          inputPlaceholder?.id;

    navigate(routePaths.ingestConfigurePlaceholder.path + route, {
      state: {
        name: name,
        desc: desc,
        pack: pack,
        objectClasses: classes,
        objectType: objectType,
        isOptional: placeholderOptional,
      },
    });
  };

  const handlePackChange = (event: any, value: any) => {
    setPack(value);
  };

  const handleClassesChange = (event: any, value: any) => {
    setClasses(value);
  };

  useEffect(() => {
    if (!inputPack)
      ingestService.GetConfigPackList().then((response) => {
        if (response.packs) setPacks(response.packs);
      });
    else setPack(inputPack);

    if (inputPlaceholder) {
      setName(inputPlaceholder?.name!);
      setDesc(inputPlaceholder?.description ?? "");
      setClasses(inputPlaceholder?.objectClasses!);
      setObjectType(inputPlaceholder?.objectType!);
      setObjectType(inputPlaceholder?.objectType!);
    }
  }, [inputPlaceholder, inputPack]);

  return (
    <Modal
      title={t("modal:title.configPlaceholder", { context: IngestConfigurePlaceholderContext[context] })}
      reference={ref}
      closeModal={closeModal}
      width={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1.5}>
            <Button onClick={closeModal}>{t("common:cancel")}</Button>
          </Stack>
          <Button
            onClick={handleCloseClick}
            variant="contained"
            disabled={!name || name.length < 3 || !objectType || !classes || classes.length == 0 || !pack}
          >
            {t("button:configurePlaceholder")}
          </Button>
        </Stack>
      }
    >
      <FormControl sx={{ mb: 3 }}>
        <InputLabel shrink htmlFor="placeholdername">
          {t("common:placeholderName")}
        </InputLabel>
        <OutlinedInput
          placeholder={t("field:placeholder.placeholderName")}
          id="placeholdername"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </FormControl>
      <FormControl sx={{ mb: 3 }}>
        <InputLabel shrink htmlFor="placeholderdesc">
          {t("field:label.placeholderDescription")}
        </InputLabel>
        <OutlinedInput
          placeholder={t("field:placeholder.placeholderDescription")}
          id="placeholderdesc"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
        />
      </FormControl>
      <Stack mb={3}>
        <Typography mb="6px">{t("common:assetType")}</Typography>
        <Select
          displayEmpty
          IconComponent={ExpandMoreRoundedIcon}
          value={objectType}
          size="small"
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          onChange={(e) => setObjectType(ObjectType[e.target.value])}
        >
          <MenuItem value={ObjectType.Video}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.Video} />
            </ListItemIcon>
            <ListItemText>{t("common:video")}</ListItemText>
          </MenuItem>
          <MenuItem value={ObjectType.Audio}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.Audio} />
            </ListItemIcon>
            <ListItemText>{t("common:audio")}</ListItemText>
          </MenuItem>
          <MenuItem value={ObjectType.Subtitle}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.Subtitle} />
            </ListItemIcon>
            <ListItemText>{t("field:placeholder.subtitle")}</ListItemText>
          </MenuItem>
          <MenuItem value={ObjectType.Image}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.Image} />
            </ListItemIcon>
            <ListItemText>{t("common:image")}</ListItemText>
          </MenuItem>
          <MenuItem value={ObjectType.ImageSequence}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.ImageSequence} />
            </ListItemIcon>
            <ListItemText>{t("menu:item.imageSequence")}</ListItemText>
          </MenuItem>
          <MenuItem value={ObjectType.Generic}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.Generic} />
            </ListItemIcon>
            <ListItemText>{t("common:generic")}</ListItemText>
          </MenuItem>
          <MenuItem value={ObjectType.DcpPackage}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.DcpPackage} />
            </ListItemIcon>
            <ListItemText>{t("menu:item.dcpPackage")}</ListItemText>
          </MenuItem>
          <MenuItem value={ObjectType.ImfPackage}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.ImfPackage} />
            </ListItemIcon>
            <ListItemText>{t("menu:item.imfPackage")}</ListItemText>
          </MenuItem>
          <MenuItem value={ObjectType.Archive}>
            <ListItemIcon>
              <AssetIcon type={ObjectType.Archive} />
            </ListItemIcon>
            <ListItemText>{t("common:archive")}</ListItemText>
          </MenuItem>
        </Select>
      </Stack>
      <Stack mb={3}>
        <Typography mb="6px">{t("field:label.assetClasses")}</Typography>
        <StyledAutocomplete
          disablePortal
          id="autocomplete-small"
          size="medium"
          options={[ObjectClass.Mezz, ObjectClass.Raw, ObjectClass.Dcp, ObjectClass.Imf, ObjectClass.Imported]}
          multiple
          sx={{ flex: 1, mb: 1 }}
          value={classes}
          onChange={(e, value) => handleClassesChange(e, value)}
          popupIcon={<SearchIcon />}
          renderInput={(params) => (
            <OutlinedInput
              {...params.InputProps}
              inputProps={params.inputProps}
              placeholder={t("field:placeholder.assetClasses")}
              sx={{ width: "100%" }}
            />
          )}
        />
      </Stack>
      <OutlinedCheckbox direction="row-reverse" checked={placeholderOptional} onClick={togglePlaceholderOptional}>
        <Typography fontWeight={500}>{t("field:helper.placeholderOptional")}</Typography>
      </OutlinedCheckbox>

      {!inputPack && (
        <>
          <Divider sx={{ mt: 3, mb: 3 }} />
          <Typography mb="6px">{t("field:label.addPlaceholderToPack")}</Typography>
          <StyledAutocomplete
            disablePortal
            id="autocomplete-small"
            size="medium"
            options={packs}
            sx={{ flex: 1, mb: 1 }}
            value={pack}
            onChange={(e, value) => handlePackChange(e, value)}
            getOptionLabel={(value: any) => value.name}
            popupIcon={<SearchIcon />}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                placeholder={t("field:placeholder.searchForPack")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </>
      )}
    </Modal>
  );
});

export default ModalCreateOrUpdatePlaceholder;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  ".MuiIconButton-root": {
    transform: "none !important",
  },
}));
